import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@elkaso-app/web-design';
import { useGetSiCategoryProductsApi, useGetSiCategoryProfileApi } from 'apis/supplier-intelligence';
import { useEffect, useState } from 'react';
import { CategoryPriceBenchmarkSection } from './category-price-benchmark-section';
import { CategoryTreeSection } from './category-tree-section';
import { DemandMetricsSection } from './demand-metrics-section';
import { OrderTrendsSection } from './order-trends-section';

interface CategoryIntelligenceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  categoryPath: string[] | null;
  categoryId: number | null;
  depth: number | null;
}

export const CategoryIntelligenceDrawer = ({
  isOpen,
  onClose,
  categoryPath,
  categoryId,
  depth,
}: CategoryIntelligenceDrawerProps) => {
  const safePath = categoryPath ?? [];
  const enabled = isOpen && safePath.length > 0;
  const [productsPage, setProductsPage] = useState(1);

  // Reset page when a different category is opened
  useEffect(() => {
    setProductsPage(1);
  }, [categoryId]);

  const { data: categoryProducts, isLoading: isProductsLoading } = useGetSiCategoryProductsApi(safePath, productsPage, {
    enabled,
  });
  const { data: profile, isLoading: isProfileLoading } = useGetSiCategoryProfileApi(categoryId, depth, { enabled });

  if (!safePath.length) return null;

  const isInitialLoading = (isProductsLoading && productsPage === 1) || isProfileLoading;
  const products = categoryProducts?.data ?? [];
  const topSuppliers = profile?.topSuppliers ?? [];
  const orderTrends = profile?.orderTrends ?? [];

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement='right' size='xl'>
      <DrawerOverlay />
      <DrawerContent maxW='65vw'>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth='1px'>
          <Text fontSize='xl' fontWeight='bold'>
            {profile?.categoryName ?? safePath[safePath.length - 1]}
          </Text>
          <CategoryTreeSection
            categories={(profile?.categoryPath ?? safePath).map((name: string, i: number) => ({ id: i, name, depth: i }))}
          />
        </DrawerHeader>

        <DrawerBody py={6}>
          {isInitialLoading ? (
            <Spinner />
          ) : (
            <>
              <Box mb={8}>
                <Text fontSize='lg' fontWeight='bold' mb={4} color='gray.700'>
                  Products in Category
                  <Badge ml={2} colorScheme='blue' fontSize='sm'>
                    {categoryProducts?.total ?? products.length}
                  </Badge>
                </Text>
                <Table variant='simple' size='sm'>
                  <Thead>
                    <Tr>
                      <Th>Product Name</Th>
                      <Th>Brand</Th>
                      <Th>Category</Th>
                      <Th>Unit</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {isProductsLoading ? (
                      <Tr>
                        <Td colSpan={4} textAlign='center' py={4}>
                          <Spinner size='sm' />
                        </Td>
                      </Tr>
                    ) : (
                      products.map((p) => (
                        <Tr key={p.productId}>
                          <Td fontWeight='medium'>{p.name}</Td>
                          <Td>{p.brand}</Td>
                          <Td>{p.category}</Td>
                          <Td>{p.unit}</Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
                {categoryProducts && categoryProducts.pageCount > 1 && (
                  <HStack justify='space-between' mt={4}>
                    <Button
                      size='sm'
                      onClick={() => setProductsPage((p) => p - 1)}
                      isDisabled={!categoryProducts.hasPrev || isProductsLoading}>
                      Previous
                    </Button>
                    <Text fontSize='sm' color='gray.600'>
                      Page {categoryProducts.page} of {categoryProducts.pageCount}
                    </Text>
                    <Button
                      size='sm'
                      onClick={() => setProductsPage((p) => p + 1)}
                      isDisabled={!categoryProducts.hasNext || isProductsLoading}>
                      Next
                    </Button>
                  </HStack>
                )}
              </Box>

              <Divider mb={8} />

              <Box mb={8}>
                <Text fontSize='lg' fontWeight='bold' mb={4} color='gray.700'>
                  Top Suppliers
                  <Badge ml={2} colorScheme='blue' fontSize='sm'>
                    {topSuppliers.length}
                  </Badge>
                </Text>
                <Table variant='simple' size='sm'>
                  <Thead>
                    <Tr>
                      <Th>Supplier</Th>
                      <Th isNumeric>Products</Th>
                      <Th isNumeric>Avg Price (AED)</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {topSuppliers.map((s) => (
                      <Tr key={s.supplierId}>
                        <Td fontWeight='medium'>{s.supplierName}</Td>
                        <Td isNumeric>{s.productCount}</Td>
                        <Td isNumeric>{(s.avgPrice ?? 0).toFixed(2)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              <Divider mb={8} />

              {profile?.priceOverview && (
                <>
                  <Box mb={8}>
                    <Text fontSize='lg' fontWeight='bold' mb={4} color='gray.700'>
                      Price Overview
                    </Text>
                    <CategoryPriceBenchmarkSection benchmark={profile.priceOverview} />
                  </Box>
                  <Divider mb={8} />
                </>
              )}

              {profile?.demand && (
                <>
                  <Box mb={8}>
                    <Text fontSize='lg' fontWeight='bold' mb={4} color='gray.700'>
                      Demand Metrics
                    </Text>
                    <DemandMetricsSection metrics={profile.demand} />
                  </Box>
                  <Divider mb={8} />
                </>
              )}

              {orderTrends.length > 0 && (
                <Box mb={8}>
                  <Text fontSize='lg' fontWeight='bold' mb={4} color='gray.700'>
                    Order Trends (12 months)
                  </Text>
                  <OrderTrendsSection trends={orderTrends} />
                </Box>
              )}
            </>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
